import React from 'react';
import { useInView } from '../hooks/useInView';
import { Package, Store, Settings } from 'lucide-react';

const capabilities = [
  {
    icon: Package,
    title: 'High-Volume Execution',
    description: 'We support multi-million-unit programs with dependable lead times and consistent quality.',
  },
  {
    icon: Store,
    title: 'Retail Channel Range',
    description: 'We build assortments for off-price, mass, department, specialty, and e-commerce partners.',
  },
  {
    icon: Settings,
    title: 'End-to-End Development',
    description: 'Design, prototyping, costing, fit, QA, packaging, and production coordination.',
  },
];

const CapabilityCard = ({ capability, delay = 0 }) => {
  const [ref, isInView] = useInView({ threshold: 0.15 });
  const Icon = capability.icon;

  return (
    <div
      ref={ref}
      className="p-8 rounded-[24px] transition-all duration-500"
      style={{
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        boxShadow: `inset 0 0 20px rgba(255,255,255,0.05),
                   inset 0 1px 1px rgba(255,255,255,0.1),
                   0px 4px 20px rgba(0,0,0,0.12)`,
        borderTop: '1px solid rgba(255,255,255,0.15)',
        borderLeft: '1px solid rgba(255,255,255,0.1)',
        borderRight: '1px solid rgba(255,255,255,0.05)',
        borderBottom: '1px solid rgba(255,255,255,0.03)',
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center mb-6">
        <Icon size={24} className="text-white/70" />
      </div>
      <h3 className="text-white text-[20px] font-medium mb-3">{capability.title}</h3>
      <p className="text-[#8a8a8a] text-[15px] leading-[1.7]">{capability.description}</p>
    </div>
  );
};

const Capabilities = () => {
  return (
    <section className="bg-[#0a0a0a] py-20 px-6">
      <div className="max-w-[1320px] mx-auto">
        {/* Section Header */}
        <div className="text-center mb-14">
          <p className="text-[#6a6a6a] text-[13px] mb-3 uppercase tracking-wider">Capabilities</p>
          <h2 className="text-[42px] md:text-[52px] font-extralight text-white mb-4 tracking-[-0.02em]">
            Built to Deliver at Scale
          </h2>
          <p className="text-[#8a8a8a] text-[15px] leading-[1.7] max-w-[550px] mx-auto">
            From concept to shelf, we support large-volume programs with speed, control, and consistency.
          </p>
        </div>

        {/* Capability Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {capabilities.map((capability, index) => (
            <CapabilityCard
              key={capability.title}
              capability={capability}
              delay={index * 100}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Capabilities;
